// Pure configuration resolution for the FAQ page.
//
// Translates notice-webview style URL params (language, edition, ...) plus a
// FAQ-specific `server` param into the values needed to talk to the Zendesk
// Help Center API: { host, locale, categoryId, platform, version, region,
// server, api, error }.
//
// Mapping tables are ported from walterArea/faq/faq.html (Confluence: URL FAQ
// 5.2 / 6.x).

import queryString from "query-string";

// Zendesk host per FAQ server.
export const HOST_MAP = {
  prod: "kinemaster.zendesk.com",
  test: "kinemaster1573111923.zendesk.com",
};

// version -> region -> platform -> server -> Help Center category id.
export const CATEGORY_MAP = {
  "6": {
    global: {
      android: { prod: "4419419755801", test: "4419435999769" },
      ios: { prod: "4419431606937", test: "4419448558105" },
    },
    // 6.x has no China variant.
  },
  "5": {
    global: {
      android: { prod: "900000007986", test: "900000016963" },
      ios: { prod: "900000008006", test: "900000016983" },
    },
    china: {
      android: { prod: "900000008026", test: "900000021706" },
      ios: { prod: "900000308446", test: "900000312186" }, // prod id removed on server
    },
  },
};

function pick(params, name, fallback) {
  const v = params[name];
  return v == null || v === "" ? fallback : v;
}

// The query language can carry a region suffix (e.g. "pt-BR", "zh-TW"), but the
// Zendesk locale slug only needs the first two letters. Two exceptions need a
// full slug: any "zh-*" variant -> Simplified Chinese ("zh-cn"), and English
// ("en") -> "en-us".
//
// china region: Simplified Chinese only for zh-* ; everything else -> en-us
export function resolveLocale(langValue, region) {
  let key = String(langValue || "").toLowerCase().slice(0, 2) || "en";
  if (key === "zh") key = "zh-cn";
  if (key === "en") key = "en-us";
  if (region === "china") {
    return key === "zh-cn" ? "zh-cn" : "en-us";
  }
  return key;
}

export function resolveConfig(search) {
  const params = queryString.parse(search);

  // server (FAQ-specific): selects the Zendesk host. notice `env` is NOT used.
  // When the `server` query is absent, fall back to the build env: the
  // test-notice bucket serves sandbox (test) data, anything else production.
  let server = pick(params, "server", null);
  if (server == null) {
    server = process.env.REACT_APP_BUCKET_NAME === "test-notice" ? "test" : "prod";
  } else {
    server = String(server).toLowerCase();
    if (server === "sandbox") server = "test";
  }

  // language: notice `language`, with legacy `lang` / `locale` fallbacks.
  const lang = pick(params, "language", pick(params, "lang", pick(params, "locale", "en")));

  // platform: notice `edition`, with legacy `platform` fallback.
  const platform = String(pick(params, "edition", pick(params, "platform", "android"))).toLowerCase();

  // back: show the header back/close button on the list (home) view. Visible by
  // default; hidden only when the host explicitly passes `back=false`.
  const back = String(pick(params, "back", "true")).toLowerCase() !== "false";

  // version: normalize "6"/"6.0"/"6.x" -> "6", "5"/"5.2" -> "5".
  const version = String(pick(params, "version", "6")).charAt(0);

  const region = String(pick(params, "region", "global")).toLowerCase();
  const categoryOverride = pick(params, "category", null);

  const locale = resolveLocale(lang, region);
  const host = HOST_MAP[server];

  // Resolve category id from the mapping table unless overridden.
  let categoryId;
  if (categoryOverride) {
    categoryId = categoryOverride;
  } else {
    const byVersion = CATEGORY_MAP[version];
    const byRegion = byVersion && byVersion[region];
    const byPlatform = byRegion && byRegion[platform];
    categoryId = (byPlatform && byPlatform[server]) || null;
  }

  let error;
  if (!host) {
    error = "server";
  } else if (!categoryId) {
    error = "category";
  }

  const api = host ? "https://" + host + "/api/v2/help_center/" + locale : null;

  return { server, host, locale, platform, version, region, back, categoryId, api, error };
}
